import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'
import { useAuthStore } from '@/stores/useAuthStore'

const LEVELS: Record<string, number> = { READ: 1, WRITE: 2, ADMIN: 3 }
const REFRESH_INTERVAL_MS = 5 * 60 * 1000

export interface BoostCoreUser {
  uuid: string
  email: string
  firstName: string | null
  lastName: string | null
  status: string
  bu?: string | null
  metadata?: {
    odoo16Id?: number | null
    odoo16PartnerId?: number | null
    luccaId?: number | null
  } | null
}

export type OdooSourceCode = 'ODOO16' | 'ODOO18'

export interface BoostCoreMeResponse {
  user: BoostCoreUser
  permissions: string[]
  featureFlags: string[]
  isManager?: boolean
  directManagedUserUuids?: string[]
  recursiveManagedUserUuids?: string[]
  authMethod?: 'msal' | 'password'
  trickster?: { active: true; targetUser: BoostCoreUser } | null
  odooSource?: OdooSourceCode
  /** URL Web Odoo de la liste des devis, construite par boostcore selon la source de l'user. */
  odooQuotesUrl?: string | null
  /** True si la BU de l'utilisateur a une préférence de validation manager configurée. */
  hasBuValidationManager?: boolean
  /** True si la BU de l'utilisateur a activé le layout V2 (ApyLayout). Remplace le flag LAYOUT_APY. */
  layoutApy?: boolean
}

/** Fallback tant que boostcore ne fournit pas l'URL (payload pas encore déployé / env absente). */
const ODOO16_QUOTES_URL_FALLBACK =
  'https://odoo.groupe-pandora.com/web#action=296&model=sale.order&view_type=list&menu_id=178'

export const useBoostCorePermissionStore = defineStore('boostCorePermission', () => {
  const suiteStore = useSuiteStore()
  const user = ref<BoostCoreUser | null>(null)
  const permissions = ref<string[]>([])
  const featureFlags = ref<string[]>([])
  const isManager = ref<boolean>(false)
  const directManagedUserUuids = ref<string[]>([])
  const recursiveManagedUserUuids = ref<string[]>([])
  const authMethod = ref<'msal' | 'password'>('msal')
  const odooSource = ref<OdooSourceCode | null>(null)
  const odooQuotesUrlFromMe = ref<string | null>(null)
  const hasBuValidationManager = ref<boolean>(false)
  const layoutApy = ref<boolean>(false)
  /** Lien externe "Devis" (Odoo) — toujours utilisable grâce au fallback ODOO16. */
  const odooQuotesUrl = computed(() => odooQuotesUrlFromMe.value ?? ODOO16_QUOTES_URL_FALLBACK)
  let refreshTimer: ReturnType<typeof setInterval> | null = null
  let notificationTimer: ReturnType<typeof setInterval> | null = null

  function startAutoRefresh() {
    if (!refreshTimer) {
      refreshTimer = setInterval(() => fetchMe(), REFRESH_INTERVAL_MS)
    }
    // Notification count polling is owned by useUserNotificationStore and
    // toggled by useNotificationWsStore based on its connected state. We don't
    // start it here to avoid a cycle (useNotificationWsStore reads the user
    // from this store).
  }

  function stopAutoRefresh() {
    if (refreshTimer) {
      clearInterval(refreshTimer)
      refreshTimer = null
    }
    if (notificationTimer) {
      clearInterval(notificationTimer)
      notificationTimer = null
    }
  }

  async function fetchMe(): Promise<BoostCoreMeResponse | null> {
    const res = await suiteStore.suiteFetch('/auth/user/me', {
      method: 'GET',
      credentials: 'include',
    })
    if (!res || !res.ok) return null
    const data: BoostCoreMeResponse = await res.json()
    user.value = data.user
    permissions.value = data.permissions ?? []
    featureFlags.value = data.featureFlags ?? []
    isManager.value = data.isManager ?? false
    directManagedUserUuids.value = data.directManagedUserUuids ?? []
    recursiveManagedUserUuids.value = data.recursiveManagedUserUuids ?? []
    authMethod.value = data.authMethod ?? 'msal'
    odooSource.value = data.odooSource ?? null
    odooQuotesUrlFromMe.value = data.odooQuotesUrl ?? null
    hasBuValidationManager.value = data.hasBuValidationManager ?? false
    layoutApy.value = data.layoutApy ?? false
    console.log(`[BoostCore] Odoo source resolved for ${data.user.email}: ${odooSource.value ?? 'unknown'}`)

    // Synchronise l'etat trickster depuis le backend (autorite unique)
    const authStore = useAuthStore()
    authStore.syncTricksterFromMe(data.trickster?.active ? data.trickster.targetUser : null)

    const notificationStore = useUserNotificationStore()
    notificationStore.fetchUnreadCount()

    startAutoRefresh()
    return data
  }

  function include(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  function hasFlag(code: string): boolean {
    return featureFlags.value.includes(code)
  }

  function can(permission: string): boolean {
    const perms = permissions.value
    if (perms.includes('SUPER_ADMIN')) return true
    const parts = permission.split('_')
    const level = parts.pop()
    const scope = parts.join('_')
    if (!level || !scope) return perms.includes(permission)
    const required = LEVELS[level]
    if (required === undefined) return perms.includes(permission)
    const matches = perms.filter((p) => p.startsWith(scope + '_'))
    if (matches.length === 0) return false
    const userLevel = Math.max(...matches.map((m) => LEVELS[m.split('_').pop() ?? ''] ?? 0), 0)
    return userLevel >= required
  }

  function clear() {
    stopAutoRefresh()
    user.value = null
    permissions.value = []
    featureFlags.value = []
    isManager.value = false
    directManagedUserUuids.value = []
    recursiveManagedUserUuids.value = []
    authMethod.value = 'msal'
    odooSource.value = null
    odooQuotesUrlFromMe.value = null
    hasBuValidationManager.value = false
  }

  const isAdv = computed(() => can('ADV_WRITE') || can('ADV_MANAGER'))
  const isAdvManager = computed(() => can('ADV_MANAGER'))
  const isSales = computed(() => can('SALES_WRITE') || can('SALES_MANAGER'))
  const isSuperAdmin = computed(() => permissions.value.includes('SUPER_ADMIN'))
  const isLead = computed(() => can('LEAD_WRITE') || can('LEAD_ADMIN') || isSuperAdmin.value)

  const buIncludesUnixo = computed(() => (user.value?.bu?.includes('UNIXO') ?? false))
  const isNegoce = computed(() => user.value?.bu === 'UNIXO_NEGOCE')

  const hasSalesAndUnixo = computed(
    () =>
      (can('SALES_READ') || include('SALES_MANAGER') || include('SALES_DIRECTOR')) &&
      buIncludesUnixo.value,
  )

  const hasSalesVariableAndUnixo = computed(
    () =>
      ((can('SALES_READ') && include('SALES_VARIABLE')) || include('SALES_MANAGER') || include('SALES_DIRECTOR')) &&
      buIncludesUnixo.value,
  )

  const isProjectRead = computed(() => include('PROJECT_READ'))
  const isProjectWrite = computed(() => include('PROJECT_WRITE'))

  const canAccessOrders = computed(() => can('ADV_READ') || hasSalesAndUnixo.value || isProjectRead.value)
  const canAccessLeads = computed(() => can('LEAD_READ') || isLead.value)
  const canCreateOrder = computed(() => include('SUPER_ADMIN') || hasSalesAndUnixo.value || isAdv.value)
  // Les commerciaux UNIXO_NEGOCE conservent l'accès régulation (bouton + listing).
  const canAccessRegulations = computed(() => can('ADV_READ') || (isNegoce.value && hasSalesAndUnixo.value))
  const canAccessVariables = computed(() => include('SUPER_ADMIN') || hasSalesVariableAndUnixo.value)
  const canAccessEligibility = computed(() => hasSalesAndUnixo.value || isAdv.value)

  return {
    user,
    permissions,
    featureFlags,
    isManager,
    directManagedUserUuids,
    recursiveManagedUserUuids,
    authMethod,
    odooSource,
    odooQuotesUrl,
    hasBuValidationManager,
    layoutApy,
    isAdv,
    isAdvManager,
    isSales,
    isSuperAdmin,
    buIncludesUnixo,
    isNegoce,
    hasSalesAndUnixo,
    isProjectRead,
    isProjectWrite,
    canAccessOrders,
    canCreateOrder,
    canAccessLeads,
    canAccessRegulations,
    canAccessVariables,
    canAccessEligibility,
    fetchMe,
    include,
    hasFlag,
    can,
    clear,
    startAutoRefresh,
    stopAutoRefresh,
  }
})
